import React, { useEffect } from 'react'
import {
  Box,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { LegalActStrings } from '@shared/consts'
import makeStyles from '@mui/styles/makeStyles'
import { BaseRequirementLegalBasis } from '@shared/types'
import { useGenerateParagraphLinks } from '@modules/admin-requirements/use-generate-paragraph-links'
import LinkStatus from '@modules/admin-requirements/link-status'
import { AuditRequirementResponse } from '@shared/types'

interface Props {
  requirement: AuditRequirementResponse
}

export const FormLegalBasis = ({ requirement }: Props) => {
  const classes = useStyles()
  const { legalBasisLinks, handleGenerateLinks } = useGenerateParagraphLinks()

  useEffect(() => {
    if (requirement.legalBasis) {
      handleGenerateLinks(
        requirement.legalBasis.map(
          (value) =>
            ({
              legalBasis: value,
              paragraph: value.paragraph,
            }) as BaseRequirementLegalBasis
        )
      )
    }
  }, [requirement.legalBasis])

  return (
    <TableContainer className={classes.legalBasisTable}>
      <Table>
        <TableHead>
          <TableRow>
            {Object.keys(LegalActStrings).map((string) => {
              const content =
                LegalActStrings[string as keyof typeof LegalActStrings]

              return (
                <TableCell key={string} size="small">
                  {content}
                </TableCell>
              )
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {requirement.legalBasis?.map((legalBasis, index) => {
            const { documentName, paragraph, details, lexApiURL } = legalBasis

            return (
              <TableRow key={legalBasis.documentName}>
                {[documentName, paragraph, details].map((basis, i, array) => {
                  const isParagraphColumn = i === 1
                  const isDetails = i === 2
                  const cellClasses =
                    i !== array.length - 1 ? classes.legalBassisNoWrapCell : ''
                  const links = isParagraphColumn
                    ? legalBasisLinks.find(
                        (legalBasisLink) => legalBasisLink.index === index
                      )
                    : undefined

                  const regex = /^(art\.\s*|§\.?\s*)\d+[a-z]*(\^\d+[a-z]*)?(-\d+[a-z]*(\^\d+[a-z]*)?)?$/i

                  const basisArray =
                    basis?.split(',').map((item) => item.trim()) ?? []
                  const { matched, others } = basisArray?.reduce(
                    (
                      acc: { matched: string[]; others: string[] },
                      item: string
                    ) => {
                      if (regex.test(item)) {
                        acc.matched.push(item)
                      } else {
                        acc.others.push(item)
                      }
                      return acc
                    },
                    { matched: [], others: [] }
                  )

                  return (
                    <TableCell size="medium" className={cellClasses}>
                      {isParagraphColumn ? (
                        <Box
                          display="flex"
                          flexDirection="column"
                          alignItems="top"
                        >
                          <Typography>
                            {lexApiURL
                              ? links?.links?.map((link, index) => (
                                  <React.Fragment key={link}>
                                    <LinkStatus link={link} isColumnView />
                                    {others.length > 0
                                      ? ', '
                                      : index < links.links.length - 1 && ', '}
                                  </React.Fragment>
                                ))
                              : basis}
                          </Typography>
                          {others.length > 0 && (
                            <Typography variant="body2" fontSize={16}>
                              {others.join(', ')}
                            </Typography>
                          )}
                        </Box>
                      ) : isDetails && lexApiURL ? (
                        <Link href={lexApiURL} target="_blank" rel="noopener">
                          {basis}
                        </Link>
                      ) : (
                        basis
                      )}
                    </TableCell>
                  )
                })}
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export const useStyles = makeStyles((theme) => {
  return {
    legalBasisTable: {
      marginBottom: theme.spacing(2),
      '& td, th': {
        paddingLeft: '0',
        fontSize: '1rem',
      },
    },
    legalBassisNoWrapCell: {
      width: 200,
    },
  }
})
